import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from '@shared/src/services/auth/auth.service';
import { LocalStorageService } from '@shared/src/services/local-storage.service';
import { from, Observable, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import {
  isValidUserRole,
  NAVIGATION_ACCESS_MAP,
  NavigationAccess,
} from '@projects/adir/src/app/layouts/private-layout/private-layout.config';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  private readonly pathToModuleMap: Record<string, string> = {
    'adir': 'adir',
    'fdp': 'adir',
    'historical-milestone-review': 'adir',
    'user-management': 'adir',
    'settings': 'adir',
    'action-items-tracker': 'adir',
  };
  constructor(
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService,
    private readonly authService: AuthService
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Check if user data exists in localStorage
    const userData = this.localStorageService.getData('user');
    if (userData) {
      return this.validateAccess(JSON.parse(userData), state.url);
    }

    // If no user data, proceed with token flow
    const idToken = this.localStorageService.getData('idToken');
    if (!idToken) {
      this.renewToken();
      return of(false);
    }

    return this.authService.getSessionToken(idToken)!.pipe(
      switchMap((res: any) => {
        this.localStorageService.saveData('user', JSON.stringify(res.user));
        return this.validateAccess(res.user, state.url);
      }),
      catchError(() => {
        // If error when checking user email/role maybe due to expired token or something
        // we will clear local storage and reload the pages to renew the tokens
        this.renewToken();
        return of(false);
      })
    );
  }

  private validateAccess(user: any, url: string): Observable<boolean> {
    try {
      const { roleName } = user;
      if (!isValidUserRole(roleName)) {
        this.renewToken();
        return of(false);
      }

      const [, firstSegment = ''] = url.split('/');
      const roleAccess = NAVIGATION_ACCESS_MAP[roleName];

      if (!firstSegment || this.isChildOfAllowedPath(firstSegment, roleAccess)) {
        return this.setModuleAndGetNewRole(firstSegment);
      }

      if (roleAccess[firstSegment as keyof typeof roleAccess]) {
        return this.setModuleAndGetNewRole(firstSegment);
      }

      this.router.navigate(['/adir']);
      return of(false);
    } catch {
      this.renewToken();
      return of(false);
    }
  }

  private setModuleAndGetNewRole(firstSegment: string) {
    this.authService.setModule(this.pathToModuleMap[firstSegment]);
    return from(this.authService.getUserInfoByModule()).pipe(
      map(() => true),
      catchError(() => {
        return of(false);
      })
    );
  }

  private renewToken() {
    localStorage.clear();
    window.location.reload();
  }

  private isChildOfAllowedPath(path: string, roleAccess: NavigationAccess): boolean {
    if (path.startsWith('adir/')) {
      return roleAccess.adir;
    }
    return false;
  }
}
